import { Flex, Skeleton, Tooltip } from 'antd'
import { type ApexOptions } from 'apexcharts'
import isUndefined from 'lodash-es/isUndefined'
import dynamic from 'next/dynamic'
import { type FunctionComponent, useMemo } from 'react'
import styled from 'styled-components'
import { LevelType } from '@/components/common/k-score/get-level'
import { mockKolPr } from '@/constants/kol-detail/metric-performance'
import { type I18nId } from '@/i18n/config'
import { useIntl } from '@/i18n/hooks/use-intl'
import type { Optional } from '@/types/optional'
import { type PerformanceQuartileMetrics } from '@/types/schema/performance-quartile-metrics-schema'
import { exhaustiveCheck } from '@/utils/exhaustive-check'
import evaluateMetricPR, {
  type RatingInfo,
} from '@/utils/kol/evaluate-metric-pr'

const ReactApexChart = dynamic(() => import('react-apexcharts'), { ssr: false })

const getFansUpRateI18nIdFromLevel = (level: LevelType): I18nId => {
  const levelToI18nId: Record<LevelType, I18nId> = {
    excellent: 'kol:platform_performance_fans_growth_pr_excellent_description',
    good: 'kol:platform_performance_fans_growth_pr_good_description',
    normal: 'kol:platform_performance_fans_growth_pr_normal_description',
    bad: 'kol:platform_performance_fans_growth_pr_bad_description',
  }

  return levelToI18nId[level]
}
const getInteractiveRateI18nIdFromLevel = (level: LevelType): I18nId => {
  const levelToI18nId: Record<LevelType, I18nId> = {
    excellent:
      'kol:platform_performance_interactive_rate_pr_excellent_description',
    good: 'kol:platform_performance_interactive_rate_pr_good_description',
    normal: 'kol:platform_performance_interactive_rate_pr_normal_description',
    bad: 'kol:platform_performance_interactive_rate_pr_bad_description',
  }

  return levelToI18nId[level]
}
const getViewRateI18nIdFromLevel = (level: LevelType): I18nId => {
  const levelToI18nId: Record<LevelType, I18nId> = {
    excellent: 'kol:platform_performance_view_rate_pr_excellent_description',
    good: 'kol:platform_performance_view_rate_pr_good_description',
    normal: 'kol:platform_performance_view_rate_pr_normal_description',
    bad: 'kol:platform_performance_view_rate_pr_bad_description',
  }

  return levelToI18nId[level]
}

const getLikeCommentRatioI18nIdFromLevel = (level: LevelType): I18nId => {
  const levelToI18nId: Record<LevelType, I18nId> = {
    excellent:
      'kol:platform_performance_like_comment_ratio_pr_excellent_description',
    good: 'kol:platform_performance_like_comment_ratio_pr_good_description',
    normal: 'kol:platform_performance_like_comment_ratio_pr_normal_description',
    bad: 'kol:platform_performance_like_comment_ratio_pr_bad_description',
  }

  return levelToI18nId[level]
}

type MetricChartType =
  | 'fansUpRate'
  | 'interactiveRate'
  | 'viewRate'
  | 'likeCommentRatio'

interface MetricChartProps {
  chartType: MetricChartType
  value: number
  prRange?: PerformanceQuartileMetrics
  isKolInfoLocked: boolean
  showMockData?: boolean
  isLoading: boolean
}

const MetricChart: FunctionComponent<MetricChartProps> = ({
  chartType,
  value,
  isKolInfoLocked,
  showMockData = false,
  isLoading,
  prRange,
}) => {
  const hiddenData = isKolInfoLocked && !showMockData
  const { formatMessage, formatNumber } = useIntl()

  const metricType = useMemo(() => {
    switch (chartType) {
      case 'fansUpRate':
        return formatMessage(
          { id: 'kol:platform_data_fans_up_rate_short_icu' },
          { capitalize: 'lowercase' },
        )
      case 'interactiveRate':
        return formatMessage(
          { id: 'kol:platform_data_avg_eng_rate_short_icu' },
          { capitalize: 'lowercase' },
        )
      case 'viewRate':
        return formatMessage(
          { id: 'kol:card_view_rate_short_icu' },
          { capitalize: 'lowercase' },
        )
      case 'likeCommentRatio':
        return formatMessage(
          {
            id: 'kol:platform_data_avg_comment_to_like_rate_icu',
          },
          { capitalize: 'lowercase' },
        )
      default:
        exhaustiveCheck(chartType)
    }
  }, [chartType, formatMessage])

  const chartTitle = useMemo(() => {
    switch (chartType) {
      case 'fansUpRate':
        return formatMessage({ id: 'kol:platform_data_fans_up_rate_short' })
      case 'interactiveRate':
        return formatMessage({ id: 'kol:platform_data_avg_eng_rate_short' })
      case 'viewRate':
        return formatMessage({ id: 'kol:card_view_rate_short' })
      case 'likeCommentRatio':
        return formatMessage({
          id: 'kol:platform_data_avg_comment_to_like_rate',
        })
      default:
        exhaustiveCheck(chartType)
    }
  }, [chartType, formatMessage])

  const data = useMemo((): RatingInfo | undefined => {
    if (isKolInfoLocked) {
      switch (chartType) {
        case 'fansUpRate':
          return mockKolPr.fansUpRatePr
        case 'interactiveRate':
          return mockKolPr.interactiveRatePr
        case 'viewRate':
          return mockKolPr.viewRatePr
        case 'likeCommentRatio':
          return mockKolPr.likeCommentRatioPr
        default:
          exhaustiveCheck(chartType)
      }
    }

    return evaluateMetricPR(value, prRange)
  }, [isKolInfoLocked, value, prRange, chartType])

  const tooltipTitle = ((): Optional<string> => {
    if (hiddenData || isUndefined(data)) {
      return undefined
    }

    const i18nIdMap: Record<MetricChartType, I18nId> = {
      fansUpRate: getFansUpRateI18nIdFromLevel(data.prRange),
      interactiveRate: getInteractiveRateI18nIdFromLevel(data.prRange),
      viewRate: getViewRateI18nIdFromLevel(data.prRange),
      likeCommentRatio: getLikeCommentRatioI18nIdFromLevel(data.prRange),
    }

    return `${formatMessage({ id: data.rating })}${formatMessage({ id: 'general:colon' })}${formatMessage({ id: i18nIdMap[chartType] })}`
  })()
  const formattedValue =
    chartType === 'likeCommentRatio'
      ? formatMessage(
          { id: 'general:like_comment_ratio' },
          {
            ratio: formatNumber(value * 100, {
              maximumFractionDigits: 2,
            }),
          },
        )
      : formatNumber(value, {
          isPercentageDisplay: true,
        })
  const formattedPr50 = ((): string => {
    if (isUndefined(prRange)) {
      return formatMessage({ id: 'autoreport:chart_label_empty_data' })
    }

    if (chartType === 'likeCommentRatio') {
      return formatMessage(
        { id: 'general:like_comment_ratio' },
        {
          ratio: formatNumber((prRange.pr50 ?? 0) * 100, {
            maximumFractionDigits: 2,
          }),
        },
      )
    }

    return formatNumber(prRange.pr50 ?? 0, {
      isPercentageDisplay: true,
    })
  })()

  const options: ApexOptions = {
    chart: {
      type: 'radialBar',
      width: 120,
      height: 120,
    },
    plotOptions: {
      radialBar: {
        startAngle: -130,
        endAngle: 130,
        hollow: {
          margin: 0,
          size: '65%',
          background: 'transparent',
        },
        dataLabels: {
          show: false,
        },
      },
    },
    fill: {
      colors: [data?.color],
    },
    // 清除 hover click 樣式
    states: {
      hover: {
        filter: {
          type: 'none',
        },
      },
      active: {
        filter: {
          type: 'none',
        },
      },
    },
  }

  return (
    <ChartWrapper>
      <ReactApexChart
        height='220px'
        options={options}
        series={[data?.pr ?? 0]}
        type='radialBar'
        width='220px'
      />

      <MetricContent vertical align='center' justify='center'>
        {isLoading ? (
          <StyledSkeleton paragraph={false} />
        ) : (
          <MetricLabel>{chartTitle}</MetricLabel>
        )}
        {isLoading ? (
          <StyledSkeleton paragraph={false} />
        ) : (
          <MetricValue isLocked={hiddenData}>{formattedValue}</MetricValue>
        )}
        {isLoading ? (
          <StyledSkeleton paragraph={false} />
        ) : (
          <Tooltip placement='bottom' title={tooltipTitle}>
            {!isUndefined(data) && (
              <StatusText color={data.color} isLocked={hiddenData}>
                {formatMessage({ id: data.rating })}
              </StatusText>
            )}
          </Tooltip>
        )}
      </MetricContent>

      {isLoading ? (
        <StyledSkeleton paragraph={false} />
      ) : (
        <DescriptionContent isLocked={hiddenData}>
          {formatMessage(
            { id: 'kol:platform_performance_chart_pr50' },
            {
              metricType,
              span: (text) => <span>{text}</span>,
              pr50: formattedPr50,
            },
          )}
        </DescriptionContent>
      )}
    </ChartWrapper>
  )
}

const ChartWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
`

const MetricContent = styled(Flex)`
  min-width: 50px;
  position: absolute;
  margin-top: 8px;
`

const StyledSkeleton = styled(Skeleton)`
  .ant-skeleton-title {
    margin-bottom: 8px !important;
  }
`

const MetricLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  color: ${({ theme }): string => theme.colors.base.grey.grey2};
  margin-bottom: 8px;
`

const MetricValue = styled.div<{ isLocked: boolean }>`
  font-size: 24px;
  font-weight: 600;
  text-align: center;
  color: ${({ theme }): string => theme.colors.base.grey.grey1};
  filter: blur(${({ isLocked }): string => (isLocked ? '5px' : '0')});
  margin-bottom: 8px;
`

const StatusText = styled.div<{ isLocked: boolean; color: string }>`
  font-size: 16px;
  font-weight: 600;
  line-height: 18.75px;
  color: ${({ color }): string => color};
  filter: ${({ isLocked }): string => (isLocked ? 'blur(4px)' : 'none')};
`

const DescriptionContent = styled.div<{ isLocked: boolean }>`
  font-size: 13px;
  font-weight: 400;
  line-height: 18.2px;
  text-align: center;
  max-width: 175px;
  color: ${({ theme }): string => theme.colors.base.grey.grey3};

  span {
    filter: blur(${({ isLocked }): string => (isLocked ? '5px' : '0')});
  }
`

export default MetricChart
